'use client';

import type { PusherIntegrationConfigType } from '@unifyapps/defs/types/interface';
import { useCallback } from 'react';
import {
  PUSHER_SUBSCRIPTION_CALLBACK_TYPES,
  type PusherCallback,
  toPusherCallback,
} from '../../consts/pusher/callbacks';
import type { PusherChannel } from '../../consts/pusher/sdk';

interface UsePusherChannelEventsParams {
  pusherIntegrationConfig?: PusherIntegrationConfigType;
  publishPusherCallback: (eventName: 'onPusherCallback', event: PusherCallback) => void;
}

export function usePusherChannelEvents({
  pusherIntegrationConfig,
  publishPusherCallback,
}: UsePusherChannelEventsParams) {
  const bindChannelEvents = useCallback(
    (pusherChannel: PusherChannel, channel: string) => {
      for (const { eventType } of pusherIntegrationConfig?.events ?? []) {
        if (!eventType) continue;
        pusherChannel.bind(eventType, (payload) => {
          publishPusherCallback('onPusherCallback', toPusherCallback(eventType, payload, channel));
        });
      }
      pusherChannel.bind('pusher:subscription_succeeded', () => {
        publishPusherCallback(
          'onPusherCallback',
          toPusherCallback(
            PUSHER_SUBSCRIPTION_CALLBACK_TYPES.subscriptionSucceeded,
            undefined,
            channel,
          ),
        );
      });
      pusherChannel.bind('pusher:subscription_error', (error) => {
        publishPusherCallback(
          'onPusherCallback',
          toPusherCallback(PUSHER_SUBSCRIPTION_CALLBACK_TYPES.subscriptionError, error, channel),
        );
      });
      pusherChannel.bind('pusher:subscription_count', (payload) => {
        publishPusherCallback(
          'onPusherCallback',
          toPusherCallback(PUSHER_SUBSCRIPTION_CALLBACK_TYPES.subscriptionCount, payload, channel),
        );
      });
    },
    [pusherIntegrationConfig?.events, publishPusherCallback],
  );

  return { bindChannelEvents };
}
